import * as React from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Cookies from "js-cookie";

import { CommonButton } from "@/components/common/buttons/CommonButton";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import Title from "@/components/molecules/Title";
import { AutoCompleteMultiple } from "@/components/molecules/AutoCompleteMultiple";
import { TextFieldForSearch } from "@/components/molecules/SearchTextField";
import { ChargerExcludeList } from "./ChargerExcludeList";
import { ChargerExcludeConstants } from "./ChargerExcludeConstants";
import { useAuthUser } from "@/hooks/useAuthUser";
import {
  SearchChargerExclude,
  ChargerExcludeSortKey,
  ChargerExcludeOrderTypes,
  ChargerExcludeModel,
  ChargerExcludeSavedState,
} from "@/models/chargerExclude";
import { Office, PLUS_CITY, ONLY_CONTRACT } from "@/models/office";
import { GX_ADMIN } from "@/models/auth";
import { getOfficesAPI } from "@/util/api/API";
import {
  getChargerExcludeAPI,
  getChargerExcludeTotalAPI,
} from "@/util/api/chargerExclude";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { MULTI_OFFICE_ID } from "@/common/CommonConstants";
import { getOfficeOptionLabel } from "@/util";

const pagingDefault = {
  page: 1,
  size: 30,
  total: 0,
};

function ChargerExcludeContent() {
  const [searchData, setSearchData] = React.useState<SearchChargerExclude>({});
  const [pagingData, setPagingData] = React.useState(pagingDefault);
  const [chargerExclude, setChargerExclude] = React.useState<
    ChargerExcludeModel[]
  >([]);
  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officesSelected, setOfficesSelected] = React.useState<Office[]>([]);
  const [orderTypes, setOrderTypes] = React.useState<ChargerExcludeOrderTypes>({
    "chargers.offices.displayName": "asc",
    "chargers.name": "asc",
  });
  const [noSerachResult, setNoSearchResult] = React.useState<boolean>(false);
  const { savedState, navigate } =
    useStatefulNavigator<ChargerExcludeSavedState>();
  const [initSearchFlag, setInitSearchFlag] = React.useState<boolean>(false);
  const { user } = useAuthUser();

  // 画面遷移先に、検索条件を保存する
  const handleClickDetail = React.useCallback(
    (id: number) => {
      navigate(`/charger_exclude/${id}`, {
        searchData,
        page: pagingData.page,
        orderTypes,
        officesSelected,
      });
    },
    [navigate, searchData, pagingData.page, orderTypes, officesSelected]
  );

  // Fetch API get list charger
  const getChargerExclude = React.useCallback(
    async (
      nextPage: number,
      search: SearchChargerExclude,
      sort?: ChargerExcludeOrderTypes
    ) => {
      try {
        const total =
          (await getChargerExcludeTotalAPI(search)).data?.count || 0;
        const res = await getChargerExcludeAPI(
          nextPage > 1 ? nextPage - 1 : 0,
          pagingData.size,
          search,
          sort
        );
        setChargerExclude(res.data);
        setPagingData({
          ...pagingDefault,
          total,
          page: nextPage,
        });
        setNoSearchResult(!res.data.length ? true : false);
      } catch (err) {
        console.log("==Error when get charger exclude data==", err);
      }
    },
    [setChargerExclude, setPagingData, pagingData.size]
  );

  //Get list office to select
  const getOffices = React.useCallback(async () => {
    try {
      const res = await getOfficesAPI(
        user?.role === GX_ADMIN ? PLUS_CITY : ONLY_CONTRACT
      );
      setOffices(res.data || []);
      const founds = res.data.filter((d) =>
        Cookies.get(MULTI_OFFICE_ID)?.split(",").includes(d.id.toString())
      );
      if (founds.length) {
        setOfficesSelected(founds);
        setSearchData({
          ...searchData,
          officeIds: founds.map(({ id }) => id),
        });
        setInitSearchFlag(true);
      } else if (savedState) {
        setInitSearchFlag(true);
      }
    } catch (err) {
      console.log("==Error when get offices==", err);
    }
  }, [user?.role, setOffices, searchData, savedState]);

  const handleChangeOffices = React.useCallback(
    (offices: Office[]) => {
      setOfficesSelected(offices);
      setSearchData({
        ...searchData,
        officeIds: offices?.map(({ id }) => id) ?? [],
      });
      Cookies.set(MULTI_OFFICE_ID, offices.map(({ id }) => id).join(","));
    },
    [searchData]
  );

  const handleChargerNames = React.useCallback(
    (name: string) => {
      setSearchData({
        ...searchData,
        chargerName: name,
      });
    },
    [searchData]
  );

  // Handle event reset search data
  const onClickResetSearchData = () => {
    setPagingData(pagingDefault);
    setSearchData({});
    setOfficesSelected([]);
  };

  // Handle event change sort type
  const onClickChangeSort = (key: ChargerExcludeSortKey) => {
    const orderTypesTemp: ChargerExcludeOrderTypes = {};
    if (key) {
      orderTypesTemp[key] =
        orderTypes[key] === ("asc" || undefined) ? "desc" : "asc";
    }
    setOrderTypes(orderTypesTemp);
    getChargerExclude(pagingData.page, searchData, orderTypesTemp);
  };

  // First load offices
  React.useEffect(() => {
    getOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // 保存された検索条件があればその条件で一覧表示
    if (savedState && initSearchFlag) {
      setSearchData(savedState.searchData || {});
      setOrderTypes(
        savedState.orderTypes || {
          "chargers.offices.displayName": "asc",
          "chargers.name": "asc",
        }
      );
      setOfficesSelected(savedState.officesSelected || []);
      getChargerExclude(
        savedState.page || 1,
        savedState.searchData || {},
        savedState.orderTypes || {
          "chargers.offices.displayName": "asc",
          "chargers.name": "asc",
        }
      );
      // Cookieの営業所IDが営業所リストに存在した場合
    } else if (!savedState && initSearchFlag) {
      getChargerExclude(1, searchData, orderTypes);
      setInitSearchFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initSearchFlag, savedState]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {ChargerExcludeConstants.ChargerExclude}
          </Title>
          <CommonButton
            btnType="Singup"
            sx={{ height: 35 }}
            onClickBtn={() => {
              navigate("/charger_exclude/create");
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} md={6} lg={6.1}>
                  <AutoCompleteMultiple
                    options={offices}
                    limit={100}
                    forSearch
                    getOptionLabel={getOfficeOptionLabel}
                    value={officesSelected}
                    uniqueKey="id"
                    onChange={handleChangeOffices}
                    label={
                      ChargerExcludeConstants.InstallationLoationOfficeName
                    }
                  />
                </Grid>
                <Grid item xs={2} md={2} lg={2.1}>
                  <TextFieldForSearch
                    label={ChargerExcludeConstants.ChargerName}
                    value={searchData.chargerName || ""}
                    setValue={handleChargerNames}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  md={4}
                  lg={3.8}
                  container
                  justifyContent="flex-end"
                >
                  <CommonButton
                    btnType="Search"
                    onClickBtn={() => {
                      getChargerExclude(1, { ...searchData }, orderTypes);
                    }}
                  />
                  <CommonButton
                    btnType="ResetSearchCriteria"
                    sx={{ ml: 2 }}
                    onClickBtn={onClickResetSearchData}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* データエリア */}
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <ChargerExcludeList
                onChangePage={(page: number) =>
                  getChargerExclude(page, { ...searchData }, orderTypes)
                }
                chargerExclude={chargerExclude}
                pagingData={pagingData}
                onClickChangeSort={onClickChangeSort}
                orderTypes={orderTypes}
                onClickDetail={handleClickDetail}
              />
              {noSerachResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default function ChargerExclude() {
  return <ChargerExcludeContent />;
}
